import React, { Component } from 'react';
import loadScript from 'load-script';
import CKEditor from "react-ckeditor-component";

const CKEDITOR_SCRIPT_URL = 'https://cdn.ckeditor.com/4.6.2/standard/ckeditor.js';

// Registers a custom "insertTag" richCombo (toolbar dropdown) once on the global CKEDITOR.
// Design ("add all once, filter on open"): the combo's init() adds ALL tags from the full
// catalog once (commit is once-only in CKEditor 4); onOpen() runs on every open and, reading
// the current editor's live applicable-group set, hides the tags/groups that don't apply to the
// template being edited. This mirrors the built-in Styles/Format combos and keeps the dropdown
// correct after the template type changes, without recreating the editor.
const insertTagPluginName = 'insertTag';

// The richCombo toolbar button and its drop-down panel both default to a narrow (~120px) width,
// which truncates the "Available tags" label and long tags like [[WorkshopBooking.SomethingLong]].
// Widen only THIS combo (its unique classes cke_combo__insertTag / cke_combopanel__insertTag) so the
// built-in Styles/Format dropdowns keep their own width. The panel width is also set imperatively in
// onOpen (below) because the floating panel gets an inline width that CSS can't always override.
const TAG_PANEL_WIDTH = 270;
// Taller panel so more tags are visible before scrolling (the skin caps the list height + scrolls).
const TAG_PANEL_HEIGHT = 420;
const injectTagPanelStyles = () => {
	if (typeof document === 'undefined' || document.getElementById('insertTag-panel-style')) {
		return;
	}
	const style = document.createElement('style');
	style.id = 'insertTag-panel-style';
	// NOTE: CKEditor lower-cases the combo name in the DOM class, so it is cke_combo__inserttag
	// (not __insertTag). The label is a separate .cke_combo_label span, whose fixed width truncates
	// the "Available tags" text — that is the element to widen, not cke_combo_text.
	// CKEditor lower-cases the combo name in the DOM class (cke_combo__inserttag). The label lives in
	// a .cke_combo_label span sized by the skin; widen the whole combo + its label/text/button so the
	// "Available tags" caption is not truncated.
	style.textContent = `
		.cke_combo__inserttag { width: auto !important; }
		.cke_combo__inserttag .cke_combo_label,
		.cke_combo__inserttag .cke_combo_text {
			width: auto !important;
			max-width: none !important;
			text-overflow: clip !important;
			overflow: visible !important;
			white-space: nowrap !important;
		}
		.cke_combo__inserttag a.cke_combo_button { width: auto !important; }
		.cke_combopanel__inserttag { width: ${TAG_PANEL_WIDTH}px !important; height: ${TAG_PANEL_HEIGHT}px !important; }
		.cke_combopanel__inserttag iframe { width: ${TAG_PANEL_WIDTH}px !important; height: ${TAG_PANEL_HEIGHT}px !important; }
		.cke_combopanel__inserttag .cke_panel_block { width: ${TAG_PANEL_WIDTH}px !important; height: ${TAG_PANEL_HEIGHT}px !important; max-height: ${TAG_PANEL_HEIGHT}px !important; overflow-y: auto !important; }
		.cke_combopanel__inserttag .cke_panel_listItem a { white-space: normal; word-break: break-all; }
	`;
	document.head.appendChild(style);
};

const registerInsertTagPlugin = (CKEDITOR, comboLabel) => {
	injectTagPanelStyles();
	if (CKEDITOR.plugins.registered[insertTagPluginName]) {
		return;
	}
	CKEDITOR.plugins.add(insertTagPluginName, {
		requires: 'richcombo',
		init(editor) {
			const allGroups = editor.config.tagAllGroups || [];
			if (!allGroups.length) {
				return; // no tags for this editor -> don't add the dropdown
			}

			// track which group labels were actually added (a group with only dynamic tags is skipped),
			// so onOpen can hide whole non-applicable groups — header label included, not just the tags.
			const addedGroups = [];

			editor.ui.addRichCombo(insertTagPluginName, {
				label: comboLabel,
				title: comboLabel,
				toolbar: 'insert',
				allowedContent: true,

				panel: {
					css: [CKEDITOR.skin.getPath('editor')],
					multiSelect: false
				},

				init() {
					allGroups.forEach(({ group, tags }) => {
						const insertable = (tags || []).filter(tag => !tag.endsWith('.*'));
						if (!insertable.length) {
							return;
						}
						this.startGroup(group);
						addedGroups.push(group);
						insertable.forEach(tag => {
							// id (used by onClick/insert) keeps the brackets; the displayed label omits them
							const value = `[[${tag}]]`;
							this.add(value, tag, tag);
						});
					});
				},

				onOpen() {
					// the floating panel gets inline width/height on first open; force our wider & taller size
					if (this._.panel && this._.panel.element) {
						const panelEl = this._.panel.element;
						panelEl.setStyle('width', `${TAG_PANEL_WIDTH}px`);
						panelEl.setStyle('height', `${TAG_PANEL_HEIGHT}px`);
						const frame = panelEl.getElementsByTag && panelEl.getElementsByTag('iframe').getItem(0);
						if (frame) {
							frame.setStyle('width', `${TAG_PANEL_WIDTH}px`);
							frame.setStyle('height', `${TAG_PANEL_HEIGHT}px`);
						}
					}
					this.showAll();
					// current applicable group names come from a live reference on the editor
					const applicable = (editor._tagApplicableGroups && editor._tagApplicableGroups()) || null;
					if (!applicable) {
						return; // no filter -> show everything
					}
					const allowed = new Set(applicable);
					// hide whole non-applicable groups (header label + their tags), not just the tags
					addedGroups.forEach(group => {
						if (!allowed.has(group)) {
							this.hideGroup(group);
						}
					});
				},

				onClick(value) {
					editor.focus();
					editor.fire('saveSnapshot');
					editor.insertText(value);
					editor.fire('saveSnapshot');
				}
			});
		}
	});
};

class CKEditorControl extends Component {
	constructor(props) {
		super(props);
		this.editor = null;
		// When the tag dropdown is used we must guarantee the plugin is registered BEFORE the
		// editor initialises (extraPlugins references it by name). The inner react-ckeditor loads
		// CKEDITOR asynchronously in its own componentDidMount, so we load it up front here, register
		// the plugin, then render the inner editor with isScriptLoaded so it skips its own load.
		this.state = { scriptLoaded: typeof window !== 'undefined' && !!window.CKEDITOR };
	}

	componentDidMount() {
		if (this.usesTags() && !this.state.scriptLoaded) {
			loadScript(CKEDITOR_SCRIPT_URL, () => {
				if (this.unmounting || !window.CKEDITOR) {
					return;
				}
				registerInsertTagPlugin(window.CKEDITOR, this.props.comboLabel || 'Tags');
				this.setState({ scriptLoaded: true });
			});
		} else if (this.usesTags() && window.CKEDITOR) {
			registerInsertTagPlugin(window.CKEDITOR, this.props.comboLabel || 'Tags');
		}
	}

	componentWillUnmount() {
		this.unmounting = true;
	}

	// keep the live applicable-groups reference fresh so the combo's onOpen sees the latest set
	componentDidUpdate() {
		if (this.editor) {
			this.editor._tagApplicableGroups = () => (this.props.applicableGroups || []).map(g => g.group);
		}
	}

	// Whether this editor is the tag-enabled one. Driven by an explicit prop, NOT by the tag array
	// length: the catalog loads asynchronously, so keying off the array would flip false->true after
	// mount and remount (blank out) the editor. tagsEnabled is stable from the first render.
	usesTags() {
		return !!this.props.tagsEnabled;
	}

	handleInstanceReady(editor) {
		this.editor = editor;
		editor._tagApplicableGroups = () => (this.props.applicableGroups || []).map(g => g.group);
		if (this.props.onInstanceReady) {
			this.props.onInstanceReady(editor);
		}
	}

	render() {
		const { content, disabled, lang, onChange, height, tagAllGroups } = this.props;
		const hasTags = this.usesTags();
		const catalogReady = hasTags && tagAllGroups && tagAllGroups.length;

		// For the tag editor, defer mounting the inner CKEditor until BOTH the CKEDITOR script is
		// loaded (so our plugin is registered before extraPlugins is read) AND the tag catalog has
		// arrived (the combo is populated once in init, so it must have the tags by then). Because
		// the editor is not mounted before this point, there is no remount/blank-out.
		if (hasTags && (!this.state.scriptLoaded || !catalogReady)) {
			return <div className="p10" />;
		}

		const config = {
			language: lang,
			readOnly: disabled,
			allowedContent: true,
			versionCheck: false,
			...(height && { height }),
			...(hasTags && {
				extraPlugins: insertTagPluginName,
				tagAllGroups
			})
		};

		return (
			<CKEditor activeClass="p10" content={content}
				isScriptLoaded={hasTags ? true : undefined}
				events={{
					change: event => onChange(event.editor.getData()),
					instanceReady: event => this.handleInstanceReady(event.editor)
				}}
				config={config} />
		);
	}
}

export default CKEditorControl;
